import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactSVG } from "react-svg";
import { TbTriangleInvertedFilled } from "react-icons/tb";
import DemoLoader from "../../component/Loader/demoLoader";
import startRecordingIcon from "../../Asset/startRecordingIcon.svg";
import restartIcon from "../../Asset/Restart.svg";
import pauseIcon from "../../Asset/Pause.svg";
import startIcon from "../../Asset/Start.svg";
import cancelIcon from "../../Asset/Cancel.svg";
import stopIcon from "../../Asset/Stop.svg";
import reuploadIcon from "../../Asset/reupload.svg";

const FREQ_BAR_COUNT = 14;
const FREQ_BAR_BAND_COUNT = FREQ_BAR_COUNT / 2;
const FREQ_BAR_STRIP_WIDTH = 78;
const FREQ_BAR_STRIP_HEIGHT = 34;
const FREQ_BAR_WIDTH_FRACTION = 0.85;
const FREQ_BAR_GAP_FRACTION = 0.5;
const FREQ_BAR_MIN_FRACTION = 0.08;
const FREQ_BAR_RMS_REF = 3500;
const FREQ_BAR_SMOOTH_ALPHA = 0.30;
const FREQ_BAR_DECAY_IDLE_MS = 300;
const FREQ_BAR_TICK_MS = 50;
const FREQ_BAR_GRADIENT_LEFT = "#3AB1F6";
const FREQ_BAR_GRADIENT_RIGHT = "#407BFF";

const FREQ_BAR_WEIGHTS = (() => {
  const weights = new Array(FREQ_BAR_COUNT);
  const half = FREQ_BAR_COUNT / 2;
  for (let i = 0; i < FREQ_BAR_COUNT; i++) {
    const bandIdx = i < half ? half - 1 - i : i - half;
    weights[i] = 1 - 0.45 * (bandIdx / (FREQ_BAR_BAND_COUNT - 1));
  }
  return weights;
})();

const VoiceWaveform = ({ voiceLevel, active, paused }) => {
  const voiceLevelRef = useRef(voiceLevel);
  voiceLevelRef.current = voiceLevel;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  const [heights, setHeights] = useState(() => new Array(FREQ_BAR_COUNT).fill(0));
  const heightsRef = useRef(heights);
  heightsRef.current = heights;

  const lastRmsAtRef = useRef(0);
  const currentRmsRef = useRef(0);

  useEffect(() => {
    if (active && !paused) {
      lastRmsAtRef.current = Date.now();
      currentRmsRef.current = Math.max(0, Math.min(1, (voiceLevel || 0) / FREQ_BAR_RMS_REF));
    } else if (paused) {
      currentRmsRef.current = 0;
    }
  }, [voiceLevel, active, paused]);

  useEffect(() => {
    if (!active) {
      const zeros = new Array(FREQ_BAR_COUNT).fill(0);
      heightsRef.current = zeros;
      setHeights(zeros);
      currentRmsRef.current = 0;
      return undefined;
    }

    const tick = () => {
      const now = Date.now();
      const idle = now - lastRmsAtRef.current > FREQ_BAR_DECAY_IDLE_MS;
      const target = idle ? 0 : currentRmsRef.current;
      const next = heightsRef.current.map((h, i) => {
        const phase = now / 110 + i * 0.62;
        const variation = 0.7 + 0.3 * Math.sin(phase);
        const weighted = target * FREQ_BAR_WEIGHTS[i] * variation;
        const v = h + (weighted - h) * FREQ_BAR_SMOOTH_ALPHA;
        return Math.abs(v) < 0.001 ? 0 : v;
      });
      heightsRef.current = next;
      setHeights(next);
    };

    const id = setInterval(tick, FREQ_BAR_TICK_MS);
    return () => clearInterval(id);
  }, [active]);

  const w = FREQ_BAR_STRIP_WIDTH;
  const h = FREQ_BAR_STRIP_HEIGHT;
  const unitWidth = w / (FREQ_BAR_COUNT * FREQ_BAR_WIDTH_FRACTION + (FREQ_BAR_COUNT - 1) * FREQ_BAR_GAP_FRACTION);
  const barWidth = unitWidth * FREQ_BAR_WIDTH_FRACTION;
  const gap = unitWidth * FREQ_BAR_GAP_FRACTION;
  const minBarPx = h * FREQ_BAR_MIN_FRACTION;
  const maxBarPx = h - 2;
  const cornerRadius = barWidth / 2;

  return (
    <svg width={w} height={h} style={{ pointerEvents: "none" }} aria-hidden="true">
      <defs>
        <linearGradient
          id="freqBarsGrad"
          x1="0"
          y1="0"
          x2={w}
          y2="0"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={FREQ_BAR_GRADIENT_LEFT} />
          <stop offset="1" stopColor={FREQ_BAR_GRADIENT_RIGHT} />
        </linearGradient>
      </defs>
      {heights.map((bh, i) => {
        const barH = minBarPx + (maxBarPx - minBarPx) * Math.min(1, Math.max(0, bh));
        const x = i * (barWidth + gap);
        const y = (h - barH) / 2;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barWidth}
            height={barH}
            rx={cornerRadius}
            ry={cornerRadius}
            fill="url(#freqBarsGrad)"
          />
        );
      })}
    </svg>
  );
};

const QUICK_LONG_PRESS_MS = 280;

const RecordingTray = ({
  loading,
  finalizing,
  recording,
  controlTrayOpen,
  retryOnlyMode,
  processingHaltedMode,
  instantTrayCollapse,
  recordingPaused,
  elapsedTime,
  voiceLevel,
  onMicClick,
  onStop,
  onRestart,
  onPauseResume,
  onCancel,
  onRetry,
  onRetryCancel,
  onProcessingCancel,
  onProcessingHaltedCancel,
  onQuickStart,
  onQuickStop,
}) => {
  const trayState = useMemo(() => {
    if (retryOnlyMode || processingHaltedMode) return "FAILED";
    if (recording && controlTrayOpen) return "EXPANDED";
    if (recording) return "COMPACT";
    return "IDLE";
  }, [controlTrayOpen, processingHaltedMode, recording, retryOnlyMode]);

  const [traySettled, setTraySettled] = useState(false);
  const [quickRecording, setQuickRecording] = useState(false);
  const longPressTimerRef = useRef(null);
  const quickRecordingRef = useRef(false);
  const onQuickStopRef = useRef(onQuickStop);
  onQuickStopRef.current = onQuickStop;
  const [showIntroTooltip, setShowIntroTooltip] = useState(
    () => !sessionStorage.getItem("zt_intro_tooltip_shown")
  );

  useEffect(() => {
    if (!quickRecording) return undefined;
    const finish = () => {
      if (!quickRecordingRef.current) return;
      quickRecordingRef.current = false;
      setQuickRecording(false);
      onQuickStopRef.current?.();
    };
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
    return () => {
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
    };
  }, [quickRecording]);

  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };
  }, []);

  const handleMicPress = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (recording || loading || finalizing) return;
    if (longPressTimerRef.current) return;
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      quickRecordingRef.current = true;
      setQuickRecording(true);
      setShowIntroTooltip(false);
      onQuickStart?.();
    }, QUICK_LONG_PRESS_MS);
  };

  const handleMicRelease = (event) => {
    if (event && event.pointerType === "mouse" && event.button !== 0) return;
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      setShowIntroTooltip(false);
      onMicClick?.();
    }
  };

  useEffect(() => {
    if (!showIntroTooltip) return;
    sessionStorage.setItem("zt_intro_tooltip_shown", "1");
    const id = setTimeout(() => setShowIntroTooltip(false), 3000);
    return () => clearTimeout(id);
  }, [showIntroTooltip]);
  const trayOpen = Boolean(controlTrayOpen || retryOnlyMode || processingHaltedMode);

  useEffect(() => {
    if (!trayOpen) {
      setTraySettled(false);
      return;
    }
    const delay = instantTrayCollapse || retryOnlyMode || processingHaltedMode ? 0 : 150;
    const id = setTimeout(() => setTraySettled(true), delay);
    return () => clearTimeout(id);
  }, [trayOpen, instantTrayCollapse, retryOnlyMode, processingHaltedMode]);

  const handleCancelPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onCancel?.();
  };
  const handleStopPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onStop?.();
  };
  const handleRestartPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onRestart?.();
  };
  const handlePauseResumePointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onPauseResume?.();
  };
  const handleMicPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setShowIntroTooltip(false);
    onMicClick?.();
  };
  const getItemRevealClass = () =>
    `transition-all duration-200 ${traySettled ? "opacity-100 translate-x-0" : "opacity-0 translate-x-1"
    }`;
  const getItemRevealStyle = (orderFromRight) => ({
    transitionDelay: traySettled ? `${orderFromRight * 55}ms` : "0ms",
  });

  return (
    <div className="shrink-0 flex justify-center items-center py-3 min-h-[76px]">
      {loading ? (
        <div className="relative w-[52px] h-[52px] flex items-center justify-center">
          <div className="scale-100 origin-center pointer-events-none">
            <DemoLoader />
          </div>
        </div>
      ) : finalizing ? (
        <div className="relative w-[52px] h-[52px] flex items-center justify-center">
          <div className="relative group w-full h-full flex items-center justify-center">
            <div className="scale-100 origin-center pointer-events-none">
              <DemoLoader />
            </div>
            <button
              type="button"
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onProcessingCancel?.();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              className="absolute inset-0 z-10 rounded-full"
              aria-label="Halt processing"
            />
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
              Halt Processing
              <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
            </span>
          </div>
        </div>
      ) : quickRecording ? (
        <div className="h-[52px] rounded-full bg-[#1B1F24] border border-[#2C323A] flex items-center gap-2 pl-4 pr-1.5">
          <div className="flex items-center">
            <VoiceWaveform
              voiceLevel={voiceLevel}
              active={true}
              paused={false}
            />
          </div>
          <div className="h-11 w-11 rounded-full bg-[#3D4651] flex items-center justify-center pointer-events-none select-none">
            <ReactSVG
              src={startRecordingIcon}
              beforeInjection={(svg) => svg.setAttribute("class", "w-9 h-9")}
              className="w-9 h-9"
            />
          </div>
        </div>
      ) : (
        <div className="relative flex items-center h-[52px]">
          <div className="relative group">
            <button
              type="button"
              onPointerDown={handleMicPress}
              onPointerUp={handleMicRelease}
              onPointerCancel={handleMicRelease}
              className={`relative z-10 w-[49px] h-[49px] rounded-full flex items-center justify-center transition-opacity duration-150 touch-none select-none ${(controlTrayOpen || retryOnlyMode || processingHaltedMode)
                  ? "opacity-0 pointer-events-none"
                  : "opacity-100"
                }`}
              aria-label={recording ? "Toggle recording controls" : "Tap to start, hold for quick recording"}
            >
              <ReactSVG
                src={startRecordingIcon}
                beforeInjection={(svg) => svg.setAttribute("class", "w-full h-full")}
                className="w-[49px] h-[49px]"
              />
            </button>
            {!recording && (
              <span className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full transition-opacity duration-200 z-30 shadow-lg pointer-events-none ${showIntroTooltip ? "opacity-100" : "opacity-0 [@media(hover:hover)]:group-hover:opacity-100"
                }`}>
                Tap to Start · Hold for Quick Record
                <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
              </span>
            )}
          </div>

          <div
            className={`absolute left-1/2 -translate-x-1/2 h-[52px] rounded-full transform-gpu transition-[max-width,opacity] ${instantTrayCollapse ? "duration-0" : "duration-500"
              } ease-in-out ${trayOpen
                ? `max-w-[360px] opacity-100 ${traySettled ? "overflow-visible pointer-events-auto" : "overflow-hidden pointer-events-none"
                }`
                : "max-w-0 opacity-0 pointer-events-none overflow-hidden"
              }`}
          >
            <div
              className={`h-full rounded-full bg-[#1B1F24] border border-[#2C323A] px-2 flex items-center gap-1.5 transition-opacity duration-150 ${traySettled ? "opacity-100" : "opacity-0"
                }`}
            >
              {retryOnlyMode || processingHaltedMode ? (
                <>
                  {retryOnlyMode && (
                    <div
                      className={`relative group ${getItemRevealClass()}`}
                      style={getItemRevealStyle(2)}
                    >
                      <button
                        type="button"
                        onPointerDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          onRetry?.();
                        }}
                        onClick={(event) => event.preventDefault()}
                        className="relative z-20 h-10 w-10 rounded-full bg-[#252A31] hover:bg-[#2F3640] transition flex items-center justify-center"
                        aria-label="Retry upload"
                      >
                        <ReactSVG
                          src={reuploadIcon}
                          beforeInjection={(svg) => svg.setAttribute("class", "w-4 h-4")}
                          className="w-4 h-4"
                        />
                      </button>
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
                        Retry
                        <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
                      </span>
                    </div>
                  )}

                  <div
                    className={`relative group ${getItemRevealClass()}`}
                    style={getItemRevealStyle(retryOnlyMode ? 1 : 1)}
                  >
                    <button
                      type="button"
                      onPointerDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (processingHaltedMode) onProcessingHaltedCancel?.();
                        else onRetryCancel?.();
                      }}
                      onClick={(event) => event.preventDefault()}
                      className="relative z-20 h-10 w-10 rounded-full bg-[#252A31] hover:bg-[#2F3640] transition flex items-center justify-center"
                      aria-label="Cancel"
                    >
                      <ReactSVG
                        src={cancelIcon}
                        beforeInjection={(svg) => svg.setAttribute("class", "w-4 h-4")}
                        className="w-4 h-4"
                      />
                    </button>
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
                      Cancel
                      <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
                    </span>
                  </div>

                  <div
                    className={`relative group ${getItemRevealClass()}`}
                    style={getItemRevealStyle(0)}
                  >
                    <button
                      type="button"
                      onPointerDown={handleMicPointerDown}
                      onClick={(event) => event.preventDefault()}
                      className="relative z-20 h-10 w-10 rounded-full bg-transparent hover:bg-transparent transition flex items-center justify-center"
                      aria-label="Start new recording"
                    >
                      <ReactSVG
                        src={startRecordingIcon}
                        beforeInjection={(svg) => svg.setAttribute("class", "w-10 h-10")}
                        className="w-10 h-10"
                      />
                    </button>
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
                      New Recording
                      <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div
                    className={`relative group ${getItemRevealClass()}`}
                    style={getItemRevealStyle(4)}
                  >
                    <button
                      type="button"
                      onPointerDown={handleStopPointerDown}
                      onClick={(event) => event.preventDefault()}
                      className="relative z-20 h-10 w-10 rounded-full bg-[#252A31] hover:bg-[#2F3640] transition flex items-center justify-center"
                      aria-label="Stop recording"
                    >
                      <ReactSVG
                        src={stopIcon}
                        beforeInjection={(svg) => svg.setAttribute("class", "w-4 h-4")}
                        className="w-4 h-4"
                      />
                    </button>
                    {traySettled && (
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
                        Stop
                        <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
                      </span>
                    )}
                  </div>

                  <div
                    className={`relative group ${getItemRevealClass()}`}
                    style={getItemRevealStyle(3)}
                  >
                    <button
                      type="button"
                      onPointerDown={handleRestartPointerDown}
                      onClick={(event) => event.preventDefault()}
                      className="relative z-20 h-10 w-10 rounded-full bg-[#252A31] hover:bg-[#2F3640] transition flex items-center justify-center"
                      aria-label="Restart recording"
                    >
                      <ReactSVG
                        src={restartIcon}
                        beforeInjection={(svg) => svg.setAttribute("class", "w-4 h-4")}
                        className="w-4 h-4"
                      />
                    </button>
                    {traySettled && (
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
                        Restart
                        <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
                      </span>
                    )}
                  </div>

                  <span
                    className={`flex items-center justify-center ${getItemRevealClass()}`}
                    style={getItemRevealStyle(2)}
                  >
                    <VoiceWaveform
                      voiceLevel={voiceLevel}
                      active={traySettled}
                      paused={recordingPaused}
                    />
                  </span>

                  <div
                    className={`relative group ${getItemRevealClass()}`}
                    style={getItemRevealStyle(1)}
                  >
                    <button
                      type="button"
                      onPointerDown={handlePauseResumePointerDown}
                      onClick={(event) => event.preventDefault()}
                      className="relative z-20 h-10 w-10 rounded-full bg-[#252A31] hover:bg-[#2F3640] transition flex items-center justify-center"
                      aria-label={recordingPaused ? "Resume recording" : "Pause recording"}
                    >
                      {recordingPaused ? (
                        <ReactSVG
                          src={startIcon}
                          beforeInjection={(svg) => svg.setAttribute("class", "w-4 h-4")}
                          className="w-4 h-4"
                        />
                      ) : (
                        <ReactSVG
                          src={pauseIcon}
                          beforeInjection={(svg) => svg.setAttribute("class", "w-4 h-4")}
                          className="w-4 h-4"
                        />
                      )}
                    </button>
                    {traySettled && (
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
                        {recordingPaused ? "Resume" : "Pause"}
                        <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
                      </span>
                    )}
                  </div>

                  <div
                    className={`relative group ${getItemRevealClass()}`}
                    style={getItemRevealStyle(0)}
                  >
                    <button
                      type="button"
                      onPointerDown={handleCancelPointerDown}
                      onClick={(event) => event.preventDefault()}
                      className="relative z-20 h-10 w-10 rounded-full bg-[#252A31] hover:bg-[#2F3640] transition flex items-center justify-center"
                      aria-label="Cancel recording"
                    >
                      <ReactSVG
                        src={cancelIcon}
                        beforeInjection={(svg) => svg.setAttribute("class", "w-4 h-4")}
                        className="w-4 h-4"
                      />
                    </button>
                    {traySettled && (
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 whitespace-nowrap bg-black text-white text-xs px-3 py-1.5 rounded-full opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg pointer-events-none">
                        Cancel
                        <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordingTray;
